import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  MuiList as List,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from "components/mui";
import HistoryIcon from "@mui/icons-material/History";
import RestoreIcon from "@mui/icons-material/Restore";
import { format, parseISO } from "date-fns";
import { useNotificationProvider } from "hooks";
import {
  GetTemplateVersions,
  GetTemplateVersionDetails,
  RestoreTemplateVersion,
} from "api/canmail/CANmailAPI";
import TemplatePreview from "../templatePreview/TemplatePreview";

const formatWhen = (iso) => {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "PPp");
  } catch {
    return "—";
  }
};

/**
 * Version history for a single template: list snapshots, preview a chosen one,
 * and restore its body onto the live template. Restoring is itself recorded as a
 * new version, so it can be undone too.
 */
function TemplateVersionHistoryModal({ open, template, onClose, onRestored }) {
  const { notifySuccess, notifyError } = useNotificationProvider();

  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [versionDetail, setVersionDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const templateId = template?.template_id;

  const loadVersions = useCallback(async () => {
    if (!templateId) return;
    setLoading(true);
    try {
      const data = await GetTemplateVersions(templateId);
      const list = Array.isArray(data) ? data : [];
      setVersions(list);
      // Auto-select the newest version
      if (list.length > 0) {
        setSelectedVersionId(list[0].version_id);
      }
    } catch (error) {
      console.error("[TemplateVersionHistoryModal] Error loading versions:", error);
      notifyError("Could not load version history.");
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [templateId, notifyError]);

  useEffect(() => {
    if (open && templateId) {
      setVersions([]);
      setSelectedVersionId(null);
      setVersionDetail(null);
      loadVersions();
    }
  }, [open, templateId, loadVersions]);

  // Load the selected version's full content for preview
  useEffect(() => {
    if (!open || !templateId || !selectedVersionId) return;
    let cancelled = false;
    (async () => {
      setLoadingDetail(true);
      try {
        const detail = await GetTemplateVersionDetails(templateId, selectedVersionId);
        if (!cancelled) setVersionDetail(detail);
      } catch (error) {
        console.error("[TemplateVersionHistoryModal] Error loading version detail:", error);
        if (!cancelled) setVersionDetail(null);
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, templateId, selectedVersionId]);

  const handleRestore = async () => {
    if (!templateId || !selectedVersionId) return;
    setRestoring(true);
    try {
      await RestoreTemplateVersion(templateId, selectedVersionId);
      notifySuccess("Template restored to the selected version.");
      onRestored?.();
      onClose();
    } catch (error) {
      console.error("[TemplateVersionHistoryModal] Error restoring version:", error);
      notifyError("Could not restore this version. Please try again.");
    } finally {
      setRestoring(false);
    }
  };

  // A pseudo-template for the preview, keyed on the version id so it re-renders per selection.
  const previewTemplate = versionDetail
    ? {
        template_id: `v-${versionDetail.version_id}`,
        rendered_html: versionDetail.rendered_html,
        template_content_text: versionDetail.template_content_text,
        template_content_drop: versionDetail.template_content_drop,
        editor_type: versionDetail.editor_type,
      }
    : null;

  const isLatest = versions.length > 0 && selectedVersionId === versions[0].version_id;

  return (
    <Dialog open={open} onClose={restoring ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <HistoryIcon />
        Version History — {template?.template_name}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, minHeight: 420 }}>
          {/* Version list */}
          <Box sx={{ width: { xs: "100%", md: 320 }, borderRight: { md: 1 }, borderColor: { md: "divider" }, maxHeight: { xs: 220, md: 560 }, overflowY: "auto" }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : versions.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No version history yet.
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {versions.map((v, idx) => (
                  <React.Fragment key={v.version_id}>
                    <ListItemButton
                      selected={v.version_id === selectedVersionId}
                      onClick={() => setSelectedVersionId(v.version_id)}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <span>Version {v.version_number}</span>
                            {idx === 0 && <Chip label="Current" size="small" color="primary" variant="outlined" />}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {v.change_note || "—"}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {formatWhen(v.created_at)}
                              {v.created_by_name ? ` · ${v.created_by_name}` : ""}
                            </Typography>
                          </>
                        }
                      />
                    </ListItemButton>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {/* Selected version preview */}
          <Box sx={{ flex: 1, p: 2, minWidth: 0 }}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Preview
            </Typography>
            {loadingDetail ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : previewTemplate ? (
              <Box sx={{ height: 440, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
                <TemplatePreview template={previewTemplate} />
              </Box>
            ) : (
              <Alert severity="info">Select a version to preview it.</Alert>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={restoring} color="primary">
          Close
        </Button>
        <Button
          onClick={handleRestore}
          variant="contained"
          color="warning"
          startIcon={<RestoreIcon />}
          disabled={restoring || !selectedVersionId || isLatest}
          title={isLatest ? "This is already the current version" : undefined}
        >
          {restoring ? "Restoring…" : "Restore this version"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TemplateVersionHistoryModal;
